import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import HeroSection from './components/HeroSection';
import ServiceCategories from './components/ServiceCategories';
import TestimonialsSection from './components/TestimonialsSection';
import TrustBadges from './components/TrustBadges';
import ServiceAreaMap from './components/ServiceAreaMap';
import PerformanceMetrics from './components/PerformanceMetrics';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleBookService = () => {
    navigate('/service-booking-wizard');
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+33123456789';
  };

  return (
    <>
      <Helmet>
        <title>PlumberPro - Services de Plomberie Professionnels 24/7 | Intervention Rapide</title>
        <meta 
          name="description" 
          content="PlumberPro - Votre expert en plomberie disponible 24/7. Réparations d'urgence, installations, maintenance. Devis transparent, techniciens certifiés. Réservez en ligne maintenant." 
        />
        <meta name="keywords" content="plomberie, plombier, urgence plomberie, réparation fuite, installation sanitaire, maintenance plomberie" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80 shadow-md">
                <Icon name="Droplet" size={24} color="#FFFFFF" />
              </div>
              <span className="text-xl font-semibold text-foreground">
                PlumberPro
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-success/10 text-success text-xs font-medium rounded-full">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
                <span>Disponible 24/7</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Phone"
                onClick={handleEmergencyCall}
                className="hidden sm:flex"
              >
                Urgence
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Calendar"
                onClick={handleBookService}
              >
                Réserver
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-16">
          <HeroSection onBookService={handleBookService} />
          <ServiceCategories onBookService={handleBookService} />
          <PerformanceMetrics />
          <TestimonialsSection />
          <TrustBadges />
          <ServiceAreaMap />
          
          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-br from-primary to-primary/80">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Prêt à résoudre votre problème de plomberie ?
              </h2>
              <p className="text-lg text-white/90 mb-8">
                Réservez votre intervention en quelques clics. Devis transparent, techniciens certifiés.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="xl"
                  iconName="Calendar"
                  onClick={handleBookService}
                  className="bg-white text-primary hover:bg-white/90"
                >
                  Réserver maintenant
                </Button>
                <Button
                  variant="outline"
                  size="xl"
                  iconName="Phone"
                  onClick={handleEmergencyCall}
                  className="border-white text-white hover:bg-white/10"
                >
                  Appel d'urgence
                </Button>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80">
                    <Icon name="Droplet" size={20} color="#FFFFFF" />
                  </div>
                  <span className="font-semibold text-foreground">PlumberPro</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Votre expert en plomberie disponible 24/7 pour tous vos besoins.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-3">Services</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Réparations d'urgence</li>
                  <li>Installations</li>
                  <li>Maintenance préventive</li>
                  <li>Débouchage</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-3">Contact</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="Phone" size={14} />
                    <span>01 23 45 67 89</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Mail" size={14} />
                    <span>contact@plumberpro.fr</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} />
                    <span>Paris et Île-de-France</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-3">Horaires</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Service disponible 24h/24, 7j/7
                </p>
                <p className="text-sm text-success font-medium">
                  Interventions d'urgence garanties
                </p>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
              <p>
                © 2026 PlumberPro. Tous droits réservés. |{' '}
                <button
                  onClick={() => navigate('/privacy-policy')}
                  className="underline hover:text-foreground transition-colors"
                >
                  Politique de confidentialité
                </button>{' '}
                | Assurance professionnelle
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;